import { Button } from '@/components/core/Button/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/core/Table';
import { useTranslation } from '@/hooks/useTranslation';
import type { DamBulkFailure, DamBulkFailureReason } from '@/pages/dam/api/damBulk';
import { damAssetPath } from '@/pages/dam/damRoutes';

interface DamBulkFailuresDialogProps {
    /** E.g. "3 of 10 assets could not be deleted". */
    title: string;
    failures: DamBulkFailure[];
    /** Puts the failed assets that still exist into the library selection. */
    onSelectFailed: (primaryKeys: string[]) => void;
    onClose: () => void;
}

const REASON_ORDER: DamBulkFailureReason[] = ['forbidden', 'conflict', 'notFound', 'unknown'];

const REASON_KEYS: Record<DamBulkFailureReason, string> = {
    forbidden: 'DAM.BULK.REASON_FORBIDDEN',
    conflict: 'DAM.BULK.REASON_CONFLICT',
    notFound: 'DAM.BULK.REASON_NOT_FOUND',
    unknown: 'DAM.BULK.REASON_UNKNOWN',
};

/**
 * Failed assets of a bulk action with the reason per asset (FR-BULK-05, slot map
 * `dam-ui-rebuild-slot-map-bulk-failures.md`): opens instead of the error toast, rows grouped
 * by reason, the file links to the detail page in a new tab.
 */
function DamBulkFailuresDialog({ title, failures, onSelectFailed, onClose }: DamBulkFailuresDialogProps) {
    const { t } = useTranslation();
    const rows = [...failures].sort((a, b) => REASON_ORDER.indexOf(a.reason) - REASON_ORDER.indexOf(b.reason));
    const selectable = failures.filter((failure) => failure.reason !== 'notFound').map((failure) => failure.primaryKey);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>{title}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-max-h-[60vh] tw-overflow-y-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="tw-bg-fill-secondary">
                                <TableHead>{t('DAM.BULK.FAILURE_FILE')}</TableHead>
                                <TableHead>{t('DAM.BULK.FAILURE_REASON')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((failure) => (
                                <TableRow key={failure.primaryKey} className="tw-bg-fill-primary">
                                    <TableCell className="tw-max-w-xs tw-truncate">
                                        {failure.reason === 'notFound' ? (
                                            <span title={failure.filename ?? failure.primaryKey}>{failure.filename ?? failure.primaryKey}</span>
                                        ) : (
                                            <a href={damAssetPath(failure.primaryKey)} target="_blank" rel="noreferrer" title={failure.filename ?? failure.primaryKey}>
                                                {failure.filename ?? failure.primaryKey}
                                            </a>
                                        )}
                                    </TableCell>
                                    <TableCell>{t(REASON_KEYS[failure.reason])}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t('CLOSE')}</Button>
                    <Button disabled={selectable.length === 0} onClick={() => onSelectFailed(selectable)}>
                        {t('DAM.BULK.SELECT_FAILED')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { DamBulkFailuresDialog };
export type { DamBulkFailuresDialogProps };
